/**
 * Typed API client for Payments — providers + payment intents.
 *
 * Providers (Stripe/MercadoPago/etc.) are configured by admins; payment
 * intents can be raised by any user and settled (mark-paid) by admins.
 * Mirrors internal/payments. Secret keys are write-only (never returned).
 */

import { apiClient } from '@/lib/apiClient';

export interface PaymentProvider {
  id: string;
  code: string;
  name: string;
  provider: string;
  publicKey?: string | null;
  enabled: boolean;
}

export interface PaymentIntent {
  id: string;
  providerId: string;
  amount?: number | null;
  currency: string;
  status: string;
  description?: string | null;
  paidAt?: string | null;
  createdAt: string;
}

export async function listProviders(): Promise<PaymentProvider[]> {
  const { data } = await apiClient.get<PaymentProvider[]>('/payments/providers');
  return data;
}

export interface CreateProviderRequest {
  code: string;
  name: string;
  provider: string;
  publicKey?: string;
  secretKey?: string;
  webhookSecret?: string;
}

export async function createProvider(req: CreateProviderRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/payments/providers', req);
  return data;
}

export async function listIntents(): Promise<PaymentIntent[]> {
  const { data } = await apiClient.get<PaymentIntent[]>('/payments/intents');
  return data;
}

export interface CreateIntentRequest {
  providerId: string;
  amount: number;
  currency: string;
  description?: string;
}

export async function createIntent(req: CreateIntentRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/payments/intents', req);
  return data;
}

export async function markIntentPaid(id: string, externalId?: string): Promise<{ ok: boolean }> {
  const { data } = await apiClient.post<{ ok: boolean }>(`/payments/intents/${id}/mark-paid`, {
    externalId: externalId || undefined,
  });
  return data;
}

// ── Stripe Checkout (Epic 226 "utilizable"): create a real hosted Checkout
//    Session and redirect the customer to Stripe's pay page. ──

export interface StripeCheckoutRequest {
  providerId: string;
  amount: number;
  currency: string;
  description: string;
  customerEmail?: string;
  entityType?: string;
  entityId?: string;
}

export interface StripeCheckoutResult {
  intentId: string;
  checkoutUrl: string;
}

export async function createStripeCheckout(
  req: StripeCheckoutRequest,
): Promise<StripeCheckoutResult> {
  const { data } = await apiClient.post<StripeCheckoutResult>('/payments/stripe/checkout', req);
  return data;
}
